import store from 'Store';
import { get } from 'Utils/common';
import { getCookie } from 'Utils/session';
import { getUtmValuesFromLocalStorage } from 'Helpers/utm';
import PROFILE_CONST from 'Const/profile.json';

export function getUniversalParams(data = {}) {
  const profileModel = store.getters['profile/profileModel'];
  const router = store.getters['router/router'];
  const prevRoute = store.getters['router/prevRoute'];

  // both 'categorySlug' and 'slug' can be the level 2 path.
  let pagePathLevel2;
  if (router.currentRoute.params.categorySlug) {
    pagePathLevel2 = router.currentRoute.params.categorySlug;
  } else if (router.currentRoute.params.slug) {
    pagePathLevel2 = router.currentRoute.params.slug;
  }

  // level 3 path is always the 'slug'. If level 2 path is the slug, then there is no level 3 path.
  const pagePathLevel3 = (pagePathLevel2 === router.currentRoute.params.slug ? undefined : router.currentRoute.params.slug);

  // Cache UTMs once and provide query-level fallback for first-hit scenarios
  const utm = getUtmValuesFromLocalStorage() || {};
  const query = router.currentRoute.query || {};

  return {
    click_text: data.text, // Get from click_event
    client_id: getCookie('_ga'), // Google Analytics 4 UID value from cookie
    destination_url: data.baseURI, // Need to change, Get from click_event
    fbc: getCookie('_fbc'), // Facebook pixel ad click UID value from cookie
    fbp: getCookie('_fbp'), // Facebook UID value from cookie
    form_id: data.form_name, // Form ID that was submitted
    form_name: data.form_name, // Form ID that was submitted
    gclid: utm.gclid || query.gclid || getCookie('gclid'), // Google Click ID (GCLID) is a parameter passed in the URL with ad clicks
    hubspot_id: getCookie('hubspotutk'),
    msclkid: utm.msclkid || query.msclkid, // parameter passed in the URL with ad clicks, to identify the campaign and other
    page_path_level_1: router.currentRoute.path, // need to fix
    page_path_level_2: pagePathLevel2, // router.currentRoute.params.categorySlug ? router.currentRoute.params.categorySlug : '',
    page_path_level_3: pagePathLevel3,
    page_referrer: window.location.origin + (prevRoute?.fullPath || ''), // Need to add this into router
    promotion_id: undefined, // Unique id for hubspot popup form or banner.
    promotion_name: undefined, // Name for hubspot popup form or banner.
    search_term: router.currentRoute.params.searchQuery, // Term that was searched in any search field. Right now only searches in the learning center
    url: router.app.$el.baseURI,
    user_id: profileModel.user_id,
    utm_campaign: utm.utm_campaign || query.utm_campaign,
    utm_content: utm.utm_content || query.utm_content,
    utm_medium: utm.utm_medium || query.utm_medium,
    utm_source: utm.utm_source || query.utm_source,
    utm_term: utm.utm_term || query.utm_term,
  };
}

export function getFarmParams(data = {}) { // AcreTrader Offering And Investment Properties
  const farmData = store.getters['farm/farmData'] || {};
  const router = store.getters['router/router'];
  const maxOwnershipDuration = farmData.ownership_duration;

  return {
    category: farmData.return_profile,
    crop_types: farmData.crop,
    currency: 'USD',
    first_investment_date: undefined,
    image_url: get(farmData, 'thumbnail.thumbnail.url', ''),
    investment_amount: data?.investment_amount, // During investment process
    investment_id: farmData.investment_id, // Offering ID
    investment_step: data?.investment_process_step, // Only during investment process.
    lifetime_value: undefined, // The sum of a user's total investments (portfolio size) multiplied by .065.
    offering_county: farmData.county,
    offering_holding_period: maxOwnershipDuration,
    offering_minimum_investment: farmData.min_shares_amount,
    offering_name: farmData.name,
    offering_state: farmData.state,
    offering_total_price: farmData.price,
    offering_count: undefined, // The total number of investments a user has successfully completed, not available currently.
    order_id: data?.investment_id, // At the end of the investment process
    payment_method: data?.payment_method, // payment_method String representing the payment method chosen to fund their investment. (wallet, wire, ach, etc.)
    position: undefined, // Position in product list
    price: (data?.investment_amount || 0) / (data?.shares || 1),
    preview: farmData.isStatusPreview,
    items: {
      category: farmData.return_profile,
      image_url: get(farmData, 'thumbnail.thumbnail.url', ''),
      name: farmData.name,
      position: undefined, // Need to pass this from farmlist.
      price: (data?.investment_amount || 0) / (data?.shares || 1),
      product_id: farmData.id,
      quantity: data?.shares, // Get during ordering process
      url: window.location.origin + router.currentRoute.path,
    },
    quantity: data?.shares, // During investment, number of shares purchased.
    revenue: data?.investment_amount ? (data.investment_amount || 0) * 0.065 : undefined, // investment_amount * .065.
    value: data?.investment_amount ? (data.investment_amount || 0) * 0.065 : undefined, // Copy of amount from revenue property.
  };
}

export function getPIIProperties(data = {}) { // AcreTrader PII Properties
  const profileModel = store.getters['profile/profileModel'];
  const currentAccount = store.getters['account/accountCurrentAccount'];
  let phone = profileModel?.phone || data?.phone || currentAccount?.personal?.phone;
  const { FINANCIAL_SITUATION } = PROFILE_CONST;

  // Adding a '1' at the begin of the phone number if it is not provided.
  if (phone && phone.length === 10) {
    phone = `1${phone}`;
  }

  const finSituation = [];
  profileModel?.financial_situation?.forEach((elem) => {
    finSituation.push(FINANCIAL_SITUATION[elem]);
  });

  return {
    address: {
      city: profileModel.city || data.city || currentAccount?.personal?.city,
      country: profileModel.country || data.country || currentAccount?.personal?.country,
      postal_code: profileModel.zip_code || data.zip_code || currentAccount?.personal?.zip_code,
      state: profileModel.state || data.state || currentAccount?.personal?.state,
      street_1: profileModel.address || data.address || currentAccount?.personal?.address,
      street_2: profileModel.address2 || data.address2 || currentAccount?.personal?.address2,
    },
    financial_situation: finSituation,
    birthday: profileModel.birthday || data.birthday || currentAccount?.personal?.birthday,
    email: profileModel.email,
    first_name: profileModel.first_name || data.first_name || currentAccount?.personal?.first_name,
    last_name: profileModel.last_name || data.last_name || currentAccount?.personal?.last_name,
    phone,
    user_city: profileModel.city || data.city || currentAccount?.personal?.city,
    user_country: profileModel.country || data.country || currentAccount?.personal?.country,
    user_postal_code: profileModel.zip_code || data.zip_code || currentAccount?.personal?.zip_code,
    user_state: profileModel.state || data.state || currentAccount?.personal?.state,
    user_street: profileModel.address || data.address || currentAccount?.personal?.address,
    user_street2: profileModel.address2 || data.address2 || currentAccount?.personal?.address2,
    hubspot_id: getCookie('hubspotutk'),
  };
}

export function getInvestmentStepParams(stepNumber, investmentData) {
  const params = { step: stepNumber, ...getPIIProperties(investmentData) };
  const currentAccount = store.getters['account/accountCurrentAccount'];

  if (stepNumber >= 1) {
    Object.assign(params, { type: investmentData?.type });
  }
  if (stepNumber >= 2) {
    Object.assign(params, { est_annual_income: investmentData.est_annual_income,
      est_liquid_worth: investmentData.est_liquid_worth,
      est_total_net_worth: investmentData.est_total_net_worth,
      type: currentAccount?.type,
      birthday: currentAccount?.personal?.birthday,
    });
  }
  if (stepNumber >= 3) {
    Object.assign(params, { employment_status: investmentData.employment_status,
      is_experienced: investmentData.is_experienced,
      is_fin_professional: investmentData.is_fin_professional,
      is_fin_regulator: investmentData.is_fin_regulator,
      objectives: investmentData.objectives,
      risk_tolerance: investmentData.risk_tolerance,
      time_horizon: investmentData.time_horizon,
      wealth: investmentData.wealth,
    });
  }

  return params;
}

export function getSignUpParams(stepNumber, signUpData) { // AcreTrader Signup Process Properties
  const { FINANCIAL_SITUATION } = PROFILE_CONST;
  const params = {
    method: 'email',
    signup_step: stepNumber,
    first_name: signUpData.first_name,
    last_name: signUpData.last_name,
    email: signUpData.email,
    confirm_privacy: signUpData.confirm_privacy,
    type: undefined, // The type of signup, e.g. invited, organic, paid, referral, direct. Not currently supported
  };
  if (stepNumber >= 2) {
    const finSituation = [];
    signUpData.financial_situation?.forEach((elem) => {
      finSituation.push(FINANCIAL_SITUATION[elem]);
    });
    Object.assign(params, { financial_situation: finSituation });
  }
  if (stepNumber >= 3) {
    const account = store.getters['account/accountCurrentAccount'];
    Object.assign(params, {
      phone: signUpData?.phone.length === 10 ? `1${signUpData.phone}` : signUpData.phone,
      accredited_signup: account ? account.accreditation_status : undefined });
  }
  return params;
}

export function getSignUpSocialParams(stepNumber, signUpData = {}) { // AcreTrader Signup Process Properties
  const { FINANCIAL_SITUATION } = PROFILE_CONST;
  const params = {
    signup_step: stepNumber,
    type: undefined, // The type of signup, e.g. invited, organic, paid, referral, direct. Not currently supported
  };
  if (stepNumber >= 3) {
    const account = store.getters['account/accountCurrentAccount'];
    const finSituation = [];
    signUpData.financial_situation?.forEach((elem) => {
      finSituation.push(FINANCIAL_SITUATION[elem]);
    });
    Object.assign(params, { financial_situation: finSituation, accredited_signup: account ? account.accreditation_status : undefined });
  }
  return params;
}

export function getProfileProperties() { // AcreTrader Login And Profile Properties
  const account = store.getters['account/accountCurrentAccount'];
  const isEmailVerified = store.getters['login/isEmailVerified'];
  const profileData = store.getters['profile/profileData'];

  return {
    accreditation_status: account ? account.accreditation_status : undefined,
    bank_link_status: account?.wallet?.isSomeLinkedBankAccount || undefined,
    email_verified: isEmailVerified,
    logins: undefined, // Total number of times a user logins, not currently supported
    method: profileData?.social, // How the user signed in.  (i.e. email, google, facebook, linkedin)
    verification_status: account?.kyc_status, // Status of a users id verification (i.e. verified, need more info., etc.)
    wallet_balance: profileData?.balance,
  };
}
